// src/components/Summenrechner.jsx
//
// Optionaler Summenrechner fuer Stationen, an denen jede Person etwas zaehlt
// (Treffer, Punkte, Gegenstaende). Wie die Stoppuhr eine reine EINGABEHILFE:
// je Teammitglied ein Zahlenfeld, die Summe wird sofort ins normale
// Ergebnisfeld geschrieben und bleibt dort von Hand editierbar. Gespeichert
// wird weiterhin nur der eine Zahlenwert (performance_score).
//
// Leere Felder zaehlen nicht – wer nicht mitgespielt hat (z. B. das fuenfte
// Teammitglied), laesst sein Feld einfach leer.

import { useRef, useState } from "react";
import { Calculator, ChevronDown } from "lucide-react";

const FALLBACK_PERSONEN = 4;

/** Summe aller ausgefuellten Felder; null, solange nichts Gueltiges drinsteht. */
export function summeVon(werte) {
  let summe = 0;
  let gefunden = false;
  for (const roh of werte) {
    const text = String(roh ?? "").trim().replace(",", ".");
    if (text === "") continue;
    const n = Number(text);
    if (!Number.isFinite(n)) continue;
    summe += n;
    gefunden = true;
  }
  // Auf zwei Nachkommastellen runden, damit 0,1 + 0,2 nicht als 0,30000000000000004 landet.
  return gefunden ? Math.round(summe * 100) / 100 : null;
}

/** Haken im Anlegen-/Bearbeiten-Dialog der Station. */
export function SummenrechnerSchalter({ checked, onChange, disabled = false, zeigeHinweis = true }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-yellow-400"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="text-sm text-gray-200">Summenrechner pro Person</span>
        {zeigeHinweis && (
          <span className="block text-xs text-gray-400">
            Im Ergebnisdialog erscheint je Teammitglied ein Feld; die Summe wird ins Ergebnisfeld
            übernommen. Sinnvoll, wenn jede Person etwas zählt.
          </span>
        )}
      </span>
    </label>
  );
}

export default function Summenrechner({ memberNames = [], unit = "", onApply, disabled = false }) {
  const namen =
    memberNames.length > 0
      ? memberNames
      : Array.from({ length: FALLBACK_PERSONEN }, (_, i) => `Person ${i + 1}`);

  const [werte, setWerte] = useState({});
  const [offen, setOffen] = useState(true);
  const feldRefs = useRef([]);

  const liste = namen.map((_, i) => werte[i] ?? "");
  const summe = summeVon(liste);
  const anzeige = summe === null ? "–" : String(summe).replace(".", ",");

  const aendern = (i, text) => {
    const neu = { ...werte, [i]: text };
    setWerte(neu);
    const neueSumme = summeVon(namen.map((_, idx) => neu[idx] ?? ""));
    // Alles geleert → Ergebnisfeld ebenfalls leeren, statt eine alte Summe stehen zu lassen.
    onApply?.(neueSumme === null ? "" : String(neueSumme));
  };

  const beiEnter = (e, i) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    feldRefs.current[i + 1]?.focus();
  };

  return (
    <div className="rounded-lg border border-primary-700/40 bg-secondary-900/60">
      <button
        type="button"
        onClick={() => setOffen((v) => !v)}
        aria-expanded={offen}
        className="flex w-full items-center justify-between gap-2 p-3 text-left"
      >
        <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary-400">
          <ChevronDown className={`h-4 w-4 transition-transform ${offen ? "" : "-rotate-90"}`} />
          <Calculator className="h-4 w-4" />
          Summe pro Person
        </span>
        <span className="text-xs text-secondary-400">
          {!offen && summe !== null ? `${anzeige}${unit ? " " + unit : ""}` : "optional"}
        </span>
      </button>

      <div className={`space-y-2 px-3 pb-3 ${offen ? "" : "hidden"}`}>
        {namen.map((name, i) => (
          <label key={i} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm text-gray-200" title={name}>
              {name}
            </span>
            <input
              ref={(el) => {
                feldRefs.current[i] = el;
              }}
              type="number"
              inputMode="decimal"
              value={werte[i] ?? ""}
              onChange={(e) => aendern(i, e.target.value)}
              onKeyDown={(e) => beiEnter(e, i)}
              disabled={disabled}
              placeholder="0"
              aria-label={`Wert für ${name}`}
              className="w-28 rounded-lg border border-secondary-700 bg-secondary-800 px-3 py-2 text-right text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </label>
        ))}

        <div className="flex items-center justify-between border-t border-secondary-700 pt-2 text-sm text-gray-300">
          <span>Summe</span>
          <span className="font-mono text-lg font-semibold text-white">
            {anzeige}
            {summe !== null && unit && unit.trim().length <= 8 ? ` ${unit.trim()}` : ""}
          </span>
        </div>
        <p className="text-xs text-secondary-400">
          Die Summe steht automatisch im Ergebnisfeld. Leere Felder zählen nicht.
        </p>
      </div>
    </div>
  );
}
